import { Context } from 'bernie-context';
import { getErrorString, Logger, SystemEvent, SystemEventLevel } from 'bernie-logger';
import { merge } from 'bernie-core';
import * as fetch from 'isomorphic-fetch';

const { withoutKeys, flattenFields } = merge;

export interface Headers {
  [name: string]: string;
}

export interface ApiResponse {
  error?: string | Error;
  errors?: string[] | Error;
}

export function bffFetch(host: string, api: string, context: Context, data: any, logger: Logger, forwardedHeaders: Headers) {
  const url = `${host}/hotel-shop/api/${context.site.id}/${context.locale}/${api}`;

  return baseFetch(url, context, data, logger, forwardedHeaders);
}

export function bffFetchPost(
  host: string,
  api: string,
  context: Context,
  data: any,
  logger: Logger,
  forwardedHeaders: Headers,
  body?: {
    [key: string]: any;
  },
  extendedUrl: boolean = true
) {
  const url = extendedUrl ? `${host}/hotel-shop/api/${context.site.id}/${context.locale}/${api}` : `${host}${api}`;

  return baseFetch(url, context, data, logger, forwardedHeaders, 'POST', body);
}

function baseFetch(
  url: string,
  context: Context,
  data: any,
  logger: Logger,
  forwardedHeaders: Headers,
  method?: 'GET' | 'POST',
  body?: {
    [key: string]: any;
  }
) {
  const query: string[] = [];
  for (const pk in data) {
    if ({}.hasOwnProperty.call(data, pk)) {
      const pkValue = data[pk];
      if (pkValue) {
        query.push(`${encodeURIComponent(pk)}=${encodeURIComponent(pkValue)}`);
      }
    }
  }
  const urlWithQuery = url + (query.length ? '?' : '') + query.join('&');

  const headers = Object.assign({}, forwardedHeaders || {}, {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'X-MC1-GUID': context.deviceId,
    'X-API-TOKEN': context.apiToken,
    'X-REMOTE-ADDR': context.remoteAddress,
    'X-USER-AGENT': context.userAgent,
    'X-USER-STATE': (context.user && context.user.state) || 'UNKNOWN'
  });

  const fetchOptions = {
    method: method || 'GET',
    headers,
    credentials: 'include'
  };
  if (method === 'POST' && body) {
    Object.assign(fetchOptions, {
      body: JSON.stringify(body)
    });
  }

  return fetch(urlWithQuery, fetchOptions)
    .then(res => {
      if (!res.status || res.status > 299) {
        // no explicit error sent back, but failure status code received
        const errorMessage = new Error(`BFF fetch response status (${res.status}) was outside range of 200-299`);
        logError(logger, errorMessage, context, urlWithQuery);
      }

      // network success with no content in response, return
      if (res.status === 204) {
        return;
      }

      // network success, pass along response body as an object from json
      return res.json().then((response: ApiResponse) => {
        // check if response contains an error message string
        if (typeof response.error === 'string') {
          // response.error should never be any other type if it exists, since it's a json response
          response.error = new Error(response.error);

          // log the error, but do not throw so we can maintain any other response we have
          logError(logger, response.error, context, urlWithQuery);
        } else if (response.errors && response.errors instanceof Array) {
          const errors = response.errors as any[];
          response.errors = new Error(JSON.stringify(response.errors));

          // log the error, but do not throw so we can maintain any other response we have
          errors.forEach(error => logError(logger, new Error(JSON.stringify(error)), context, urlWithQuery, error.level));
        }

        return response;
      });
    })
    .catch(err => {
      // network failure or json parse failure on response body, log error but do not re-throw
      logError(logger, err, context, urlWithQuery);

      // no valid data response to include with return
      return {
        error: err
      };
    });
}

function logError(logger: Logger, error: Error, context: Context, fetchedUrl = '', level?: SystemEventLevel) {
  if (logger && error instanceof Error) {
    if (typeof context === 'object' && typeof context.toLOG === 'function') {
      context = context.toLOG() as Context;
    }

    const requestMessage = `requested_url="${fetchedUrl}"`;
    const flatContext = flattenFields(withoutKeys(['logger']).merge({}, context));
    const errorMessage = getErrorString(error);

    logger.logEvent(new SystemEvent(level || SystemEventLevel.ERROR, 'BFF_FETCH_ERROR'), errorMessage, requestMessage, flatContext);
  }
}
